/**
 * AuthVerify Page
 * Handles magic link verification callback
 * URL format: /auth/verify?token=ABC&redirect=/path
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';

interface VerifyMagicLinkResponse {
  token: string;
  expires_at: string;
  applicant: {
    id: string;
    email: string;
  };
}

export function AuthVerify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');
      const redirect = searchParams.get('redirect') || '/my-projects';
      // Extract pathname if redirect is a full URL, otherwise use as-is
      const redirectPath = redirect.startsWith('http') 
        ? new URL(redirect).pathname 
        : redirect;

      if (!token) {
        setError('No verification token provided');
        setVerifying(false);
        return;
      }

      try {
        // Call backend to verify magic link token
        const response = await apiClient.post<VerifyMagicLinkResponse>(
          '/auth/verify-magic-link',
          { token }
        );

        // Store JWT in localStorage
        localStorage.setItem('applicant_auth_token', response.token);
        localStorage.setItem('applicant_auth_expires_at', response.expires_at);

        // Update AuthContext state
        await login();

        // Redirect to specified page
        navigate(redirectPath, { replace: true });
      } catch (err) {
        console.error('Verification failed:', err);
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to verify magic link. The link may be expired or invalid.'
        );
        setVerifying(false);
      }
    };

    verifyToken();
  }, [searchParams, navigate, login]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="card max-w-md text-center">
          <div className="flex justify-center mb-6">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            Verifying your account
          </h1>
          <p className="text-neutral-600">
            Please wait while we verify your magic link...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="card max-w-md text-center">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            Verification Failed
          </h1>
          <p className="text-neutral-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/')} className="btn-secondary">
              Go Home
            </button>
            <button onClick={() => window.location.reload()} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
